import { useRef } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { CoachingWorkspaceItem } from "@bw/shared/api";
import { useCreateCoachingGuardianInvitation } from "@bw/shared/hooks";

const purposes = {
  view_proposal: "Review proposals", accept_terms: "Accept coaching terms", pay_for_learner: "Pay for coaching",
  view_sessions: "View coaching sessions", book_sessions: "Book coaching sessions",
} as const;
const schema = z.object({
  purposes: z.array(z.enum(["view_proposal", "accept_terms", "pay_for_learner", "view_sessions", "book_sessions"])).min(1, "Select at least one permission"),
  expires: z.string().min(1, "Choose an invitation deadline").refine((value) => Number.isFinite(new Date(value).getTime()), "Enter a valid deadline"),
});

export function CoachingGuardianInvite({ requestId, guardian }: { requestId: string; guardian: NonNullable<CoachingWorkspaceItem["guardian"]> }) {
  const invite = useCreateCoachingGuardianInvitation();
  const command = useRef<{ fingerprint: string; key: string } | null>(null);
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { purposes: [], expires: "" } });
  const selected = form.watch("purposes");
  if (!guardian.can_invite_guardian) return <Typography variant="body2">Guardian permission: {guardian.status}. The learner&apos;s guardians manage this invitation in Family approvals; any one of them can accept or revoke it.</Typography>;
  return <Stack component="form" spacing={1} onSubmit={form.handleSubmit((values) => {
    const body = { purposes: values.purposes, invitation_expires_at: new Date(values.expires).toISOString() };
    const fingerprint = JSON.stringify(body);
    if (command.current?.fingerprint !== fingerprint) command.current = { fingerprint, key: crypto.randomUUID() };
    invite.mutate([requestId, { ...body, idempotency_key: command.current.key }]);
  })}>
    <Typography variant="body2">Request permission from the learner&apos;s guardians. Any of the learner&apos;s verified guardians can accept or revoke these permissions.</Typography>
    {Object.entries(purposes).map(([key, label]) => <FormControlLabel key={key} label={label} control={<Checkbox
      checked={selected.includes(key as keyof typeof purposes)} disabled={invite.isPending}
      onChange={(_, checked) => form.setValue("purposes", checked ? [...selected, key as keyof typeof purposes] : selected.filter((value) => value !== key), { shouldValidate: true })} />} />)}
    <TextField label="Invitation deadline (your local time)" size="small" type="datetime-local" {...form.register("expires")} slotProps={{ inputLabel: { shrink: true } }} disabled={invite.isPending}
      error={Boolean(form.formState.errors.expires)} helperText={form.formState.errors.expires?.message} />
    {form.formState.errors.purposes ? <Alert severity="warning">{form.formState.errors.purposes.message}</Alert> : null}
    {invite.isError ? <Alert severity="error">The invitation was not confirmed. Check the selected permissions and deadline, then retry.</Alert> : null}
    <Button size="small" type="submit" variant="contained" disabled={invite.isPending} sx={{ alignSelf: "flex-start" }}>{invite.isPending ? "Inviting…" : "Invite guardian"}</Button>
  </Stack>;
}
